import type { TFunction } from "i18next";
import type { TSetupPhase, TSetupProgress } from "@/zapi-frontend/shared/hooks/useProjectSetupPolling";

const MINUTE_MS = 60 * 1000;
// SETUP_ETA_SECONDS on the backend, for a run that has not reported its own ETA yet.
const DEFAULT_SETUP_ETA_SECONDS = 3 * 60 * 60;

export function setupMinutesLeft(setup: TSetupProgress | null, now: number): number {
  const etaMs = (setup?.etaSeconds ?? DEFAULT_SETUP_ETA_SECONDS) * 1000;
  const startedAt = setup ? new Date(setup.startedAt).getTime() : NaN;
  const msLeft = Number.isFinite(startedAt) ? startedAt + etaMs - now : etaMs;
  return Math.min(Math.ceil(msLeft / MINUTE_MS), Math.ceil(etaMs / MINUTE_MS));
}

export function formatMinutesLeft(t: TFunction<"projectHome">, minutesLeft: number): string {
  const hours = Math.floor(minutesLeft / 60);
  const minutes = minutesLeft % 60;
  if (hours === 0) return t("generation.readyInMinutes", { minutes });
  if (minutes === 0) return t("generation.readyInHours", { hours });
  return t("generation.readyInHoursMinutes", { hours, minutes });
}

export function setupPhaseLabelKey(phase: TSetupPhase): `generation.setupPhase.${TSetupPhase}` {
  return `generation.setupPhase.${phase}`;
}

const KNOWN_TOOLS = new Set([
  "Read",
  "Write",
  "Edit",
  "MultiEdit",
  "Bash",
  "Grep",
  "Glob",
  "Task",
  "TodoWrite",
  "WebFetch",
  "WebSearch",
  "NotebookEdit",
  "done",
  "processing"
]);

export function modelNarration(lastAction: string | null | undefined): string | null {
  const text = (lastAction ?? "").trim();
  if (!text) return null;
  const separator = text.indexOf(": ");
  const prefix = separator > 0 ? text.slice(0, separator) : text;
  if (KNOWN_TOOLS.has(prefix)) return null;
  return text;
}
